import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {SummaryDTO} from '../../dto/summary';
import {selectSelectedProject} from '../../../project/service/project.selectors';
import {deleteSummary, stopGenerateProjectSummary} from '../../services/summary/summary.actions';
import {
  selectError,
  selectLoading,
  selectSummaryState,
  selectSummaryProcessRunning
} from '../../services/summary/summary.selectors';
import {MatDialog} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatToolbarModule} from '@angular/material/toolbar';
import {RouterLink} from '@angular/router';
import {CreateSummaryDialogComponent} from '../create-summary-dialog/create-summary-dialog.component';
import {ConfirmDialogComponent} from '../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';
import {EmptyStateComponent} from '../../../../utils/components/empty-state/empty-state.component';
import {HeaderComponent} from '../../../../utils/components/header/header.component';

@Component({
  selector: 'app-summary-overview',
  imports: [
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    MatToolbarModule,
    RouterLink,
    MatMenuModule,
    LoadingCardComponent,
    EmptyStateComponent,
    HeaderComponent
  ],
  templateUrl: './summary-overview.component.html',
  styleUrl: './summary-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SummaryOverviewComponent {
  private readonly store: Store = inject(Store);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);

  displayedColumns: string[] = ['name', 'topic', 'actions'];

  readonly project = this.store.selectSignal(selectSelectedProject);
  private readonly summaryState = this.store.selectSignal(selectSummaryState);

  readonly projectId = computed(() => this.project()?.id);
  readonly summaries = computed<SummaryDTO[] | undefined>(() => {
    const projectId = this.projectId();
    return projectId ? this.summaryState().projectSummary[projectId] : undefined;
  });
  readonly loading = this.store.selectSignal(selectLoading);
  readonly error = this.store.selectSignal(selectError);
  readonly summaryProcessRunning = this.store.selectSignal(selectSummaryProcessRunning);
  readonly editable = computed(() =>
    this.projectPermissionService.hasWritePermission(this.project() ?? undefined)
  );


  createSummary(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(CreateSummaryDialogComponent, {
      data: {
        projectId
      }
    });
  }

  deleteSummary(summary: SummaryDTO): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.store.dispatch(deleteSummary({projectId, summaryId: summary.id}));
  }

  stopSummaryProcess(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Stop Analysis',
        message: 'Do you want to stop the creating analysis process?',
        confirmButtonText: 'Stop'
      }
    }).afterClosed().subscribe((result) => {
      if (!result) return;
      this.store.dispatch(stopGenerateProjectSummary({projectId}));
    });
  }

  protected readonly stop = stop;
}
